import React, { useEffect, useRef, useState } from 'react';
import { atom, useSetAtom } from 'jotai';
import { useInViewport } from 'ahooks';
import { RoccoLink } from '../../../components/link/RoccoLink';
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from '../../../components/accordion';
import { Grid } from '../../layout/components/Grid';
import { NavigationMenu } from './NavigationMenu';
import { DisplayMode } from '../../../types/enum/display-mode';
import { uiStateAtomReachedFooter } from '../states/ui-states-reached-footer';
import { ReactComponent as APLogoBlack } from '../../../icons/svg/logo-new-black.svg';
import { LogoAnimated } from '../../../components/logo-animated/LogoAnimated';
import { CountUp } from '../../../components/count-up';
import { CustomArrowRightThick } from '@archipro-design/icons';

interface FooterTrackingConfig {
    onNavClick: (link: string) => void;
    onEmailSubscribe: (email: string) => void;
    onBottomLinkSectionCardClick: (index: number) => void;
}

export const uiFooterTrackingConfig = atom((get) =>
    get(localFooterTrackingConfigAtom)
);
const localFooterTrackingConfigAtom = atom<FooterTrackingConfig | undefined>(
    undefined
);

export interface FooterProps {
    subscriptionForm?: React.ReactNode;
    linksColumn1: FooterLinks[];
    linksColumn2: FooterLinks[];
    websiteTermLink: string;
    privacyPolicyLink: string;
    displayMode: DisplayMode;
    trackingConfig?: FooterTrackingConfig;
    isLegacyBrokenRemMode?: boolean;
    hideContactArchiPro?: boolean;
    currentCountryCode?: string;
    monthlyUser: {
        uniqueUsers: number;
        lastUpdated: string;
    };
    newZealandHomepageUrl?: string;
    australiaHomepageUrl?: string;
}

interface FooterLinks {
    title: string;
    links: Array<{
        title: string;
        to: string;
        isNonRemixRoute?: boolean;
        newTab?: boolean;
    }>;
}

export const Footer = (props: FooterProps) => {
    const { displayMode, trackingConfig } = props;
    const setTrackingConfig = useSetAtom(localFooterTrackingConfigAtom);

    useEffect(() => {
        setTrackingConfig(trackingConfig);
    }, [trackingConfig, setTrackingConfig]);

    if (displayMode === DisplayMode.Desktop) {
        return <DesktopFooter {...props} />;
    }

    return <MobileFooter {...props} />;
};

const MobileFooter = (props: FooterProps) => {
    const {
        subscriptionForm,
        linksColumn1,
        linksColumn2,
        websiteTermLink,
        privacyPolicyLink,
        hideContactArchiPro,
        currentCountryCode,
        isLegacyBrokenRemMode,
    } = props;

    const containerRef = useRef<HTMLDivElement>(null);
    const [isVisible] = useInViewport(containerRef, {
        rootMargin: '-120px',
    });

    const setReachedFooter = useSetAtom(uiStateAtomReachedFooter);
    const newLinksColumn2 = hideContactArchiPro
        ? removeContactUsLink(linksColumn2)
        : linksColumn2;

    const newLinksColumn1 = removeHomeDesignEveningLink(
        linksColumn1,
        currentCountryCode
    );

    useEffect(() => {
        setReachedFooter(!!isVisible);
    }, [isVisible, setReachedFooter]);

    const privacyLinks = {
        title: 'Privacy',
        links: [
            {
                title: 'Terms',
                to: websiteTermLink,
            },
            {
                title: 'Privacy Policy',
                to: privacyPolicyLink,
            },
        ],
    };

    return (
        <footer ref={containerRef} className="bg-gray-100 pt-20">
            <section className="px-6">
                {subscriptionForm && (
                    <div className="border-y border-gray-200 py-4 pb-12">
                        {subscriptionForm}
                    </div>
                )}

                <Accordion type="single" collapsible>
                    <div className="mb-8">
                        <h3 className="my-5 text-lg font-medium leading-[1.3] tracking-[0.025rem]">
                            For Homeowners
                        </h3>
                        {newLinksColumn1.map((link) => (
                            <AccordionItem key={link.title} value={link.title}>
                                {/* Need to explicitly set font family for non-remix pages */}
                                <AccordionTrigger className="font-sans">
                                    {link.title}
                                </AccordionTrigger>
                                <AccordionContent>
                                    <NavigationMenu {...link} />
                                </AccordionContent>
                            </AccordionItem>
                        ))}
                    </div>

                    <div>
                        <h3 className="my-5 text-lg font-medium leading-[1.3] tracking-[0.025rem]">
                            For Businesses
                        </h3>
                        {newLinksColumn2.map((link) => (
                            <AccordionItem key={link.title} value={link.title}>
                                {/* Need to explicitly set font family for non-remix pages */}
                                <AccordionTrigger className="font-sans">
                                    {link.title}
                                </AccordionTrigger>
                                <AccordionContent>
                                    <NavigationMenu {...link} />
                                </AccordionContent>
                            </AccordionItem>
                        ))}
                    </div>

                    <AccordionItem
                        key={privacyLinks.title}
                        value={privacyLinks.title}
                    >
                        <AccordionTrigger className="font-sans">
                            {privacyLinks.title}
                        </AccordionTrigger>
                        <AccordionContent>
                            <NavigationMenu {...privacyLinks} />
                        </AccordionContent>
                    </AccordionItem>
                </Accordion>

                <div className="pb-17.5 pt-37">
                    <RoccoLink to="/" aria-label="Home">
                        <LogoAnimated
                            size={4}
                            mode="disabled"
                            isLegacyBrokenRemMode={isLegacyBrokenRemMode}
                        />
                    </RoccoLink>
                </div>
            </section>
        </footer>
    );
};

const DesktopFooter = (props: FooterProps) => {
    const {
        subscriptionForm,
        linksColumn1,
        linksColumn2,
        websiteTermLink,
        privacyPolicyLink,
        hideContactArchiPro,
        currentCountryCode,
        monthlyUser,
        newZealandHomepageUrl,
        australiaHomepageUrl,
    } = props;

    const containerRef = useRef<HTMLDivElement>(null);
    const [isVisible] = useInViewport(containerRef, {
        rootMargin: '-120px',
    });

    const setReachedFooter = useSetAtom(uiStateAtomReachedFooter);

    // Two-stage animation state
    const [hasReachedFooterEnd, setHasReachedFooterEnd] = useState(false);
    const [showWhiteDiv, setShowWhiteDiv] = useState(false);
    const [whiteDivHeight, setWhiteDivHeight] = useState<number | null>(null);
    const [footerEndScrollPosition, setFooterEndScrollPosition] = useState<
        number | null
    >(null);
    const whiteDivRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleScroll = () => {
            if (!containerRef.current) return;

            const rect = containerRef.current.getBoundingClientRect();
            const windowHeight = window.innerHeight;
            const footerBottom = rect.bottom;

            // Stage 1: Check if we've reached the end of the footer (footer bottom at viewport bottom)
            if (footerBottom <= windowHeight && !hasReachedFooterEnd) {
                setHasReachedFooterEnd(true);
                setShowWhiteDiv(true); // Show white div immediately when footer end is reached
                setFooterEndScrollPosition(window.scrollY); // Record the scroll position when footer end is reached
            }

            // Reset if user scrolls back up before footer end
            if (footerBottom > windowHeight && hasReachedFooterEnd) {
                setHasReachedFooterEnd(false);
                setShowWhiteDiv(false);
                setWhiteDivHeight(null); // Reset height measurement
                setFooterEndScrollPosition(null); // Reset scroll position tracking
            }
        };

        window.addEventListener('scroll', handleScroll, { passive: true });
        window.addEventListener('resize', handleScroll);
        handleScroll(); // Initial calculation

        return () => {
            window.removeEventListener('scroll', handleScroll);
            window.removeEventListener('resize', handleScroll);
        };
    }, [
        hasReachedFooterEnd,
        showWhiteDiv,
        whiteDivHeight,
        footerEndScrollPosition,
    ]);

    // Measure white div height when it becomes visible
    useEffect(() => {
        if (showWhiteDiv && whiteDivRef.current && whiteDivHeight === null) {
            // Use requestAnimationFrame to ensure the div is fully rendered
            requestAnimationFrame(() => {
                if (whiteDivRef.current) {
                    const height = whiteDivRef.current.offsetHeight;
                    setWhiteDivHeight(height);
                }
            });
        }
    }, [showWhiteDiv, whiteDivHeight]);

    useEffect(() => {
        setReachedFooter(!!isVisible);
    }, [isVisible, setReachedFooter]);

    const newLinksColumn2 = hideContactArchiPro
        ? removeContactUsLink(linksColumn2)
        : linksColumn2;

    const newLinksColumn1 = removeHomeDesignEveningLink(
        linksColumn1,
        currentCountryCode
    );

    const [hasInitialAnimation, setHasInitialAnimation] = useState(false);
    const [previousCount, setPreviousCount] = useState<number | undefined>(
        undefined
    );

    const { uniqueUsers: monthlyUserCount, lastUpdated } = monthlyUser;

    // Trigger animation when monthlyUserCount prop changes
    useEffect(() => {
        if (
            monthlyUserCount !== undefined &&
            previousCount !== undefined &&
            monthlyUserCount !== previousCount
        ) {
            setHasInitialAnimation(true);
        }
        setPreviousCount(monthlyUserCount);
    }, [monthlyUserCount, previousCount]);

    const monthlyUsersCount = {
        value: monthlyUserCount ?? 350000, // Fallback to 350000 if not provided
        startValue: hasInitialAnimation ? (monthlyUserCount ?? 350000) - 20 : 0, // Start from 0 on first load, then from current-20 on updates
        duration: 2000,
        delay: 0,
    };

    return (
        <div className="relative">
            {/* Fixed white div that stays stationary - only visible when animation starts */}
            {showWhiteDiv && (
                <div
                    ref={whiteDivRef}
                    className="fixed bottom-0 left-0 right-0 z-0 bg-white px-12 py-16"
                >
                    <div className="w-full font-display text-20 font-semibold leading-[100%]">
                        <div className="text-center text-[#989898]">
                            We Believe Great Design Is The Ultimate
                        </div>
                        <div className="text-left">
                            <span className="inline text-[#989898]">
                                Competitive Advantage.
                            </span>
                            &nbsp;
                            <span className="inline text-black">
                                Our Platform Helps Homeowners, Brands, And
                                Architects Connect, Stand Out, And Succeed.
                            </span>
                        </div>
                    </div>
                </div>
            )}

            {/* Footer with two-stage animation */}
            <footer
                ref={containerRef}
                className="relative z-10 bg-[#DCDCDC] px-12"
                style={{
                    transform: (() => {
                        if (
                            !hasReachedFooterEnd ||
                            !whiteDivHeight ||
                            footerEndScrollPosition === null
                        ) {
                            return 'translateY(0px)';
                        }

                        // Calculate scroll distance since footer end
                        const scrolledSinceFooterEnd =
                            window.scrollY - footerEndScrollPosition;

                        // Clamp to never exceed white div height
                        const clampedDistance = Math.min(
                            Math.max(0, scrolledSinceFooterEnd),
                            whiteDivHeight
                        );

                        return `translateY(${-clampedDistance}px)`;
                    })(),
                    transition: hasReachedFooterEnd
                        ? 'transform 0.1s ease-out'
                        : 'none',
                }}
            >
                <div className="h-auto min-h-[280px] py-12 qhd:mx-auto qhd:max-w-[1920px] qhd:px-12">
                    {/* Logo row: logo on the left, text box on the right */}
                    <div className="mb-25 flex items-center gap-x-14">
                        <div>
                            <RoccoLink
                                to="/"
                                prefetch="intent"
                                aria-label="Home"
                            >
                                <APLogoBlack className="h-70 w-92.5 !fill-[#222222]" />
                            </RoccoLink>
                        </div>
                        <div className="flex h-70 items-end">
                            <div>
                                <div className="mb-16 max-w-[35%] text-base font-medium text-[#161616]">
                                    Trusted by millions of homeowners,
                                    architects, and suppliers shaping the built
                                    environment
                                </div>
                                <div className="block text-15 font-medium leading-[1.1] -tracking-[0.02em] text-[#161616]">
                                    3.9 million people · 12 million page views
                                    annually.
                                </div>
                                <div className="-mb-1.5 block text-15 font-medium leading-none -tracking-[0.02em] text-[#8E8E8E]">
                                    {`Monthly Users in ${new Date(lastUpdated ?? undefined).toLocaleString('default', { month: 'long' })} so far `}
                                    {monthlyUsersCount ? (
                                        <CountUp
                                            value={monthlyUsersCount.value}
                                            startValue={
                                                monthlyUsersCount.startValue ??
                                                1000
                                            }
                                            duration={
                                                monthlyUsersCount.duration ??
                                                2000
                                            }
                                            delay={monthlyUsersCount.delay ?? 0}
                                            startOnView={true}
                                            formatter={(val) =>
                                                val.toLocaleString()
                                            }
                                            numberClassName="font-medium"
                                        />
                                    ) : (
                                        '350,000'
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    <Grid colClasses="h-full grid-cols-1 md:grid-cols-4">
                        <div className="flex flex-col justify-between">
                            <div className="mt-[5.875rem]">
                                {subscriptionForm ?? <div />}

                                {/* "We are global but local" section directly beneath subscription, spanning 2 columns */}
                                <div className="mt-25 md:col-span-2 md:w-[calc(200%+1.5rem)]">
                                    <h3 className="mb-10 font-display text-base font-normal leading-[130%] tracking-wide text-black">
                                        We are global but local.
                                    </h3>
                                    <div className="flex flex-col gap-2 font-display leading-[100%]">
                                        {newZealandHomepageUrl && (
                                            <RoccoLink
                                                to={newZealandHomepageUrl}
                                                className="inline-flex items-center gap-2 whitespace-nowrap text-20 font-semibold text-[#323232] hover:text-[#323232]/80"
                                                isNonRemixRoute={true}
                                                newTab={true}
                                            >
                                                New Zealand
                                                <CustomArrowRightThick className="mt-2 h-18 w-20 -rotate-45 justify-end overflow-visible !fill-[#323232]" />
                                            </RoccoLink>
                                        )}
                                        {australiaHomepageUrl && (
                                            <RoccoLink
                                                to={australiaHomepageUrl}
                                                className="inline-flex items-center gap-2 whitespace-nowrap text-20 font-semibold text-[#323232] hover:text-[#323232]/80"
                                                isNonRemixRoute={true}
                                                newTab={true}
                                            >
                                                Australia
                                                <CustomArrowRightThick className="mt-2 h-18 w-20 -rotate-45 justify-end overflow-visible !fill-[#323232]" />
                                            </RoccoLink>
                                        )}
                                        <div className="inline-flex h-20 items-center gap-2 whitespace-nowrap text-20 font-semibold text-[#AEAEAE]">
                                            America
                                            <div className="ml-2 flex h-15 flex-col justify-end text-base leading-none text-[#AEAEAE]">
                                                <span>Coming</span>
                                                <span>Soon</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="border-r border-gray-900/10"></div>

                        <div className="flex flex-col justify-between border-r border-gray-900/10">
                            <div className="mb-8 flex flex-col gap-y-14">
                                <h3 className="mb-5 text-lg font-normal tracking-wide text-gray-900">
                                    For Homeowners
                                </h3>

                                {newLinksColumn1.map((linksSection, index) => (
                                    <NavigationMenu
                                        key={index}
                                        {...linksSection}
                                    />
                                ))}
                            </div>

                            <span className="text-base text-gray-900/50">
                                © ArchiPro {new Date().getFullYear()}
                            </span>
                        </div>

                        <div className="flex flex-col justify-between">
                            <div className="mb-8 flex flex-col gap-y-14">
                                <h3 className="mb-5 text-lg font-normal tracking-wide text-gray-900">
                                    For Businesses
                                </h3>

                                {newLinksColumn2.map((linksSection, index) => (
                                    <NavigationMenu
                                        key={index}
                                        {...linksSection}
                                    />
                                ))}
                            </div>

                            <div className="flex flex-col gap-y-1">
                                <RoccoLink
                                    to={websiteTermLink}
                                    className="text-base text-gray-900/50 hover:text-gray-900/50"
                                    isNonRemixRoute={true}
                                >
                                    Terms
                                </RoccoLink>

                                <RoccoLink
                                    to={privacyPolicyLink}
                                    className="text-base text-gray-900/50 hover:text-gray-900/50"
                                    isNonRemixRoute={true}
                                >
                                    Privacy Policy
                                </RoccoLink>
                            </div>
                        </div>
                    </Grid>
                </div>
            </footer>

            {/* Extra space to allow scrolling - exactly matches white div height to prevent over-scrolling */}
            <div
                className="bg-transparent"
                style={{
                    height: `${whiteDivHeight || 200}px`,
                }}
            ></div>
        </div>
    );
};

/**
 * Removes any links to the contact-us page from a column of footer links.
 * Used to conditionally hide the Contact ArchiPro link in the footer.
 *
 * @param linksColumn - Array of footer link groups to filter
 * @returns A new array with contact-us links removed from each group
 */
const removeContactUsLink = (linksColumn: FooterLinks[]) => {
    return linksColumn.map((group) => ({
        ...group,
        links: group.links.filter((link) => link.to !== '/contact-us'),
    }));
};

/**
 * Removes "Home Design Evening" links from a column of footer links when currentCountryCode is 'US'.
 * Used to conditionally hide the Home Design Evening link in the footer for US users.
 *
 * @param linksColumn - Array of footer link groups to filter
 * @param currentCountryCode - The current country code
 * @returns A new array with Home Design Evening links removed if currentCountryCode is 'US'
 */
const removeHomeDesignEveningLink = (
    linksColumn: FooterLinks[],
    currentCountryCode?: string
) => {
    if (currentCountryCode !== 'US') return linksColumn;

    return linksColumn.map((group) => ({
        ...group,
        links: group.links.filter(
            (link) => link.title !== 'Home Design Evening'
        ),
    }));
};
